import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrganizationCompanyModel } from '@domain/organization-company/models/organization-company.model';
import { CompanySelectorService } from '@core/services/presenters/company-selector.service';
import { LocalStorageService } from '@core/services/infrastructure/localstorage.service';
import { STORAGE_USER_PERSON } from '@shared/utils/constants/storage.constant';
import { IconWrapperComponent } from '@shared/design/molecules/icon-wrapper/icon-wrapper.component';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { getModuleListUrl } from '@shared/utils/functions/url.function';

@Component({
  selector: 'app-company-selector',
  imports: [CommonModule, FormsModule, IconWrapperComponent],
  templateUrl: './company-selector.component.html',
  styleUrl: './company-selector.component.scss',
})
export class CompanySelectorComponent implements OnInit {
  isOpen = signal(false);
  searchTerm = signal('');
  allCompanies = signal<OrganizationCompanyModel[]>([]);
  filteredCompanies = signal<OrganizationCompanyModel[]>([]);

  constructor(
    private elementRef: ElementRef,
    private companySelectorService: CompanySelectorService,
    private queryClient: QueryClient,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.loadCompaniesFromStorage();
  }

  private loadCompaniesFromStorage(): void {
    try {
      const stored = this.localStorageService.getItem(STORAGE_USER_PERSON);
      if (!stored) return;
      const userPerson = JSON.parse(stored);
      const companies: OrganizationCompanyModel[] =
        userPerson?.organizationCompanys ?? [];
      this.allCompanies.set(companies);
      this.filteredCompanies.set(companies);
    } catch {}
  }

  get selectedCompany() {
    return this.companySelectorService.selectedCompany;
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
    if (this.isOpen()) {
      this.searchTerm.set('');
      this.filteredCompanies.set(this.allCompanies());
    }
  }

  selectCompany(company: OrganizationCompanyModel): void {
    this.isOpen.set(false);
    this.companySelectorService.select(company);

    const listUrl = getModuleListUrl(window.location.pathname);
    if (listUrl) {
      window.location.href = listUrl;
    } else {
      window.location.reload();
    }
  }

  onSearch(term: string): void {
    this.searchTerm.set(term);
    this.filteredCompanies.set(this.filterByTerm(this.allCompanies()));
  }

  hasOtherCompanies(): boolean {
    return this.filteredCompanies().some((c) => !this.isSelected(c));
  }

  isSelected(company: OrganizationCompanyModel): boolean {
    return this.selectedCompany()?.refId === company.refId;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
    }
  }

  private filterByTerm(
    companies: OrganizationCompanyModel[],
  ): OrganizationCompanyModel[] {
    const term = this.searchTerm().toLowerCase();
    if (!term) return companies;
    return companies.filter(
      (c) =>
        c.tradeName?.toLowerCase().includes(term) ||
        c.businessName?.toLowerCase().includes(term),
    );
  }
}
